import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import Loading from "../common/Loading";
import { PageShell, api, STATUS_LABELS, PrimaryButton, verificationStatus } from "./shared";
import StudioShell from "./StudioShell";
import ApplicationWizard from "./ApplicationWizard";

function IntroScreen({ onGetStarted }) {
  return (
    <div className="max-w-2xl">
      <p className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-3">Artist Studio</p>
      <h1 className="text-[32px] font-bold text-gray-900 mb-4 leading-tight">Turn your music into income on Nyawoo</h1>
      <p className="text-gray-500 mb-6">
        Upload your catalogue, license it to creators and businesses, and get paid directly — all without leaving
        Nyawoo.
      </p>
      <ul className="space-y-3 mb-8">
        {[
          "Upload and protect your catalogue with automatic rights fingerprinting",
          "Set your own licensing prices for personal, creator and business use",
          "Track earnings and cash out to your bank from your Nyawoo wallet",
        ].map((text) => (
          <li key={text} className="flex gap-2 text-gray-800">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gray-800 shrink-0" />
            <span>{text}</span>
          </li>
        ))}
      </ul>
      <PrimaryButton onClick={onGetStarted}>Get Started</PrimaryButton>
    </div>
  );
}

const SUBMITTED_STEPS = [
  { label: "Application received", state: "done" },
  { label: "Verification in review", state: "active" },
  { label: "Payout setup confirmed", state: "todo", number: 3 },
];

function StepIcon({ state, number }) {
  if (state === "done") {
    return (
      <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs shrink-0" style={{ backgroundColor: "#2a7a47" }}>
        ✓
      </span>
    );
  }
  if (state === "active") {
    return (
      <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] shrink-0" style={{ backgroundColor: "#fdf3e7", color: "#c26a1a" }}>
        •••
      </span>
    );
  }
  return (
    <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-gray-400 shrink-0" style={{ backgroundColor: "#eeeeee" }}>
      {number}
    </span>
  );
}

// FSD 6.6: the status carries the review date (submitted + 2 business days). Before it: "Expected decision by Fri, Oct 23, 2026."
// After it, with no decision yet: an apology instead of a date in the past. No date from the server: nothing is shown.
export function decisionNote(slaDueAt, now = new Date()) {
  const due = slaDueAt ? new Date(slaDueAt) : null;
  if (!due || Number.isNaN(due.getTime())) return null;
  if (due.getTime() < now.getTime()) return "This is taking longer than usual. Thank you for your patience.";
  return `Expected decision by ${due.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })}.`;
}

// Shown once an application has been submitted and is waiting for review.
function SubmittedView({ applicationId, status, slaDueAt }) {
  const note = decisionNote(slaDueAt);
  return (
    <div className="w-full min-h-[60vh] flex flex-col items-center justify-center text-center py-8">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: "#e8f8ee" }}>
        <span className="text-2xl" style={{ color: "#2a7a47" }}>✓</span>
      </div>
      <h1 className="text-[28px] leading-tight font-bold text-gray-900 mb-2">Application submitted</h1>
      <p className="text-gray-500 text-sm mb-8">
        We're verifying your identity — most artists hear back within 2 business days.
      </p>
      {note && <p className="text-gray-700 text-sm font-medium -mt-5 mb-8" data-testid="application-decision-note">{note}</p>}
      <ul className="w-full max-w-md mx-auto text-left rounded-2xl border border-gray-200 bg-white px-6 py-5 space-y-4">
        {SUBMITTED_STEPS.map((step) => (
          <li key={step.label} className="flex items-center gap-4">
            <StepIcon state={step.state} number={step.number} />
            <span className={`text-sm ${step.state === "todo" ? "text-gray-400 font-medium" : "font-semibold text-gray-900"}`}>
              {step.label}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-gray-500 text-sm mt-6">Application submitted successfully. Your artist account application is currently pending review.</p>
      {applicationId && (
        <p className="text-gray-400 text-sm mt-1">
          Application ID: <span className="font-medium text-gray-600">#{applicationId}</span>
          {" · "}Status: <span className="font-medium text-gray-600">{STATUS_LABELS[status] || status}</span>
        </p>
      )}
    </div>
  );
}

// The "You're verified" welcome screen is shown once, the first time an approved artist opens Artist Studio.
const welcomeKey = (applicationId) => `pipaan_artist_welcome_seen_${applicationId}`;
function hasSeenWelcome(applicationId) {
  try {
    return window.localStorage.getItem(welcomeKey(applicationId)) === "1";
  } catch (e) {
    return true; // storage unavailable: skip the one-time screen rather than show it on every visit
  }
}
function markWelcomeSeen(applicationId) {
  try {
    window.localStorage.setItem(welcomeKey(applicationId), "1");
  } catch (e) {
    /* ignore */
  }
}

function VerifiedWelcome({ onContinue }) {
  return (
    <div className="w-full min-h-[60vh] flex flex-col items-center justify-center text-center py-12">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: "#e8f8ee" }}>
        <span className="text-2xl" style={{ color: "#2a7a47" }}>✓</span>
      </div>
      <h1 className="text-[28px] leading-tight font-bold text-gray-900 mb-2">You're verified</h1>
      <p className="text-gray-500 text-sm mb-6">Welcome to Artist Studio. Start by uploading your first track.</p>
      <PrimaryButton onClick={onContinue}>Go to Artist Studio</PrimaryButton>
    </div>
  );
}

const TONES = {
  gray: "bg-gray-100 text-gray-700",
  amber: "bg-amber-50 text-amber-800",
  green: "bg-emerald-50 text-emerald-800",
  red: "bg-red-50 text-red-700",
};

function StatusPill({ application }) {
  const st = verificationStatus(application);
  return (
    <p className="flex items-center justify-center gap-2 text-sm text-gray-500">
      Verification status
      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${TONES[st.tone]}`} data-testid="verification-status">{st.label}</span>
    </p>
  );
}

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

// Every earlier submission stays on record; this lists them for the artist.
// The review team stores a rejection reason as "<Heading>: <message>" (for example "Missing information: Please add your tax form").
// Splits it so the heading can stand on its own line. A reason without a known heading (older ones) is returned whole.
const REASON_HEADINGS = [
  "Invalid document", "Missing information", "Information mismatch", "Document expired",
  "Duplicate artist account", "Verification requirements not met", "Other",
];
export function splitRejectionReason(text) {
  const value = String(text || "").trim();
  const heading = REASON_HEADINGS.find((h) => value.startsWith(`${h}: `));
  return heading ? { heading, message: value.slice(heading.length + 2).trim() } : { heading: "", message: value };
}

function ReasonText({ reason, className = "" }) {
  const { heading, message } = splitRejectionReason(reason);
  return (
    <>
      {heading && <p className={`font-semibold ${className}`}>{heading}</p>}
      {message && <p className={`whitespace-pre-line ${className}`}>{message}</p>}
    </>
  );
}

function SubmissionHistory({ items }) {
  if (!items || items.length < 2) return null;
  return (
    <section className="mt-8 text-left max-w-xl mx-auto" aria-label="Previous submissions">
      <h3 className="font-bold text-gray-900 mb-3">Submission history</h3>
      <ul className="divide-y divide-gray-100 rounded-2xl border border-gray-200 bg-white">
        {items.map((h) => {
          const st = verificationStatus({ status: h.status, resubmission_required: h.resubmission_required });
          return (
            <li key={h.id} className="px-5 py-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-700">Application #{h.id} · {formatDate(h.submitted_at)}</span>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${TONES[st.tone]}`}>{st.label}</span>
              </div>
              {h.rejection_reason && (
                <div className="mt-1" data-testid={`history-reason-${h.id}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Reason</p>
                  <ReasonText reason={h.rejection_reason} className="text-gray-600" />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function RejectedView({ application, onResubmit }) {
  const needsResubmission = !!application.resubmission_required;
  return (
    <div className="w-full min-h-[60vh] flex flex-col items-center justify-center text-center py-8">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: "#fdeeee" }}>
        <span className="text-2xl text-red-600">!</span>
      </div>
      <h1 className="text-[28px] leading-tight font-bold text-gray-900 mb-3">
        {needsResubmission ? "Resubmission required" : "Application not approved"}
      </h1>
      <p className="text-gray-500 md:text-lg mb-6">
        {needsResubmission
          ? "We need a few corrections before we can verify you."
          : "We couldn't verify your artist account with this application."}
      </p>
      <div className="w-full max-w-xl mx-auto text-left rounded-2xl border border-amber-200 bg-amber-50 px-6 py-5 mb-8">
        <p className="font-semibold text-amber-900">Reason from our review team</p>
        {application.rejection_reason ? (
          <div className="mt-1"><ReasonText reason={application.rejection_reason} className="text-amber-900" /></div>
        ) : (
          <p className="text-amber-900 mt-1">No reason was provided. Please check your details and documents, then submit again.</p>
        )}
      </div>
      <PrimaryButton onClick={onResubmit}>Update and resubmit</PrimaryButton>
    </div>
  );
}

function VerifiedView({ application }) {
  return (
    <div className="text-center py-10 max-w-xl mx-auto">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: "#e8f8ee" }}>
        <span className="text-2xl" style={{ color: "#2a7a47" }}>✓</span>
      </div>
      <h1 className="text-[28px] leading-tight font-bold text-gray-900 mb-3">Your account is verified</h1>
      <p className="text-gray-500 md:text-lg mb-6">
        Your artist identity has been confirmed, so there is nothing more to submit.
        {application.reviewed_at ? ` Verified on ${formatDate(application.reviewed_at)}.` : ""}
      </p>
      <StatusPill application={application} />
      <div className="mt-6">
        <Link to="/artist-studio/profile" className="inline-block rounded-full font-semibold text-white px-8 py-3" style={{ backgroundColor: "#143328" }}>
          Go to Artist Profile
        </Link>
      </div>
    </div>
  );
}

// `verificationPage` = opened from Artist Studio > Verification. Everyone sees their verification
// status there; an approved artist sees the Verified state and cannot submit again.
export default function ArtistApplication({ verificationPage = false }) {
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [application, setApplication] = useState(null);
  const [history, setHistory] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const navigate = useNavigate();

  const loadHistory = async () => {
    try {
      const response = await api.get("history");
      if (response.data?.success) setHistory(response.data.result || []);
    } catch (error) {
      // History is a nice-to-have; the status screen works without it.
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get("status");
        if (!cancelled && response.data?.success) setApplication(response.data.result || null);
      } catch (error) {
        // A failed status check should not block the person from applying - fall back to the intro/form.
        console.error("Failed to load artist application status:", error);
      } finally {
        if (!cancelled) setLoadingStatus(false);
      }
    })();
    loadHistory();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadingStatus) {
    return (
      <PageShell>
        <div className="w-full flex items-center justify-center py-24">
          <Loading />
        </div>
      </PageShell>
    );
  }

  const status = application?.status;

  if (status === "approved") {
    if (verificationPage) {
      return (
        <StudioShell active="verification">
          <VerifiedView application={application} />
        </StudioShell>
      );
    }
    // Opened from the studio entry page: show the one-time welcome, then go to the profile.
    if (welcomeDismissed || hasSeenWelcome(application.id)) {
      return <Navigate to="/artist-studio/profile" replace />;
    }
    return (
      <PageShell>
        <VerifiedWelcome
          onContinue={() => {
            markWelcomeSeen(application.id);
            setWelcomeDismissed(true);
            navigate("/artist-studio/profile");
          }}
        />
      </PageShell>
    );
  }

  const isRejected = status === "rejected";
  let body;
  if (status === "pending") {
    body = <SubmittedView applicationId={application.id} status={application.status} slaDueAt={application.sla_due_at} />;
  } else if (showForm) {
    body = (
      <ApplicationWizard
        initial={isRejected ? application : null}
        onCancel={() => setShowForm(false)}
        onSubmitted={(a) => {
          setApplication(a);
          setShowForm(false);
          loadHistory();
          window.scrollTo({ top: 0 });
        }}
      />
    );
  } else if (isRejected) {
    body = <RejectedView application={application} onResubmit={() => setShowForm(true)} />;
  } else {
    body = <IntroScreen onGetStarted={() => setShowForm(true)} />;
  }

  // The intro and the application form sit at the left of the page, as in the design; the status screens stay centred.
  const leftAligned = showForm || !(status === "pending" || isRejected);
  return (
    <PageShell>
      <div className={`w-full py-6 ${showForm ? "max-w-xl" : leftAligned ? "max-w-2xl" : "max-w-2xl mx-auto"}`}>
        {!showForm && verificationPage && <div className="mb-6"><StatusPill application={application} /></div>}
        {body}
        {!showForm && <SubmissionHistory items={history} />}
      </div>
    </PageShell>
  );
}
